import { useQueries } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import { useSurveysGateway } from './SurveysGatewayProvider';
import {
  getCurrentSurveyByGroupQueryOptions,
  getMySurveyResponsesV2QueryOptions,
  hasRespondedToSurvey,
} from '../api/survey';
import { isVisitUnexpired, type SurveyVisits } from '../state/surveyVisits';
import { useSurveyVisitsStore } from '../state/surveyVisitsStore';

import { useUserScope } from '@/hooks/useUserScope';
import { useUserScopedQuery } from '@/hooks/useUserScopedQuery';

export type ResumableSurvey = {
  groupId: string;
  name: string;
};

const NO_VISITS: SurveyVisits = {};

/**
 * Lists every survey the user opened but never submitted, so the Account
 * screen can offer a way back to each, and counts the ones still worth
 * badging over.
 *
 * Visiting is what makes a survey resumable: an unanswered survey the user
 * has never opened is not something they can be invited to "continue".
 *
 * `badgeCount` is deliberately not `pending.length`. Once the user follows the
 * badge into a survey from the Account screen, the nudge has done its job and
 * retires, while the row stays so they can still finish. A badge that outlives
 * the trip it was pointing at is one the user learns to ignore.
 *
 * The offer expires a week after that first visit. A prompt the user has
 * walked past for a week is one they have declined in practice, and an
 * indefinite badge would train them to ignore it.
 */
export function useResumableSurveys() {
  const { userId, canFetch } = useUserScope();

  // The store is a module singleton, so an account switch can leave the
  // previous identity's visits in place until hydration catches up.
  const visits = useSurveyVisitsStore((state) =>
    state.hydratedUserId === userId ? state.visits : NO_VISITS
  );

  const acknowledged = useSurveyVisitsStore((state) =>
    state.hydratedUserId === userId ? state.acknowledged : NO_VISITS
  );

  const hydrate = useSurveyVisitsStore((state) => state.hydrate);

  const gateway = useSurveysGateway();

  useEffect(() => {
    if (!userId) {
      return;
    }

    hydrate(userId).catch(() => {});
  }, [userId, hydrate]);

  const unexpiredGroupIds = useMemo(
    () =>
      Object.entries(visits)
        .filter(([, visitedAt]) => isVisitUnexpired(visitedAt))
        .map(([groupId]) => groupId),
    [visits]
  );

  const surveyResults = useQueries({
    queries: unexpiredGroupIds.map((groupId) => ({
      ...getCurrentSurveyByGroupQueryOptions(groupId, gateway),
      enabled: canFetch,
    })),
  });

  const { data: responses } = useUserScopedQuery(getMySurveyResponsesV2QueryOptions, [gateway], {
    enabled: unexpiredGroupIds.length > 0,
  });

  // useQueries returns a new array each render, so the resolved survey ids are
  // what the memo can actually key on.
  const surveyKey = surveyResults.map((result) => result.data?.id ?? '').join('|');

  const pending = useMemo(() => {
    if (!responses) {
      return [];
    }

    return unexpiredGroupIds.reduce<ResumableSurvey[]>((resumable, groupId, index) => {
      const survey = surveyResults[index]?.data;

      if (survey && !hasRespondedToSurvey(responses, survey.id)) {
        resumable.push({ groupId, name: survey.name });
      }

      return resumable;
    }, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unexpiredGroupIds, responses, surveyKey]);

  const badgeCount = useMemo(
    () => pending.filter((survey) => !acknowledged[survey.groupId]).length,
    [pending, acknowledged]
  );

  return { pending, badgeCount };
}
